import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AnalyticsContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Generate anonymous session ID
function generateSessionId() {
    const stored = sessionStorage.getItem('analytics_session_id');
    if (stored) return stored;
    const newId = 'sess_' + Math.random().toString(36).substr(2, 9) + Date.now().toString(36);
    sessionStorage.setItem('analytics_session_id', newId);
    return newId;
}

// Detect device type
function getDeviceType() {
    const ua = navigator.userAgent;
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
        return 'tablet';
    }
    if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
        return 'mobile';
    }
    return 'desktop';
}

export function AnalyticsProvider({ children }) {
    const [consentGiven, setConsentGiven] = useState(null);
    const [sessionId] = useState(generateSessionId);
    const [deviceType] = useState(getDeviceType);

    // Check for existing consent on mount
    useEffect(() => {
        const raw = localStorage.getItem('analytics_consent');
        if (!raw) return; // null = never decided, show banner
        try {
            const data = JSON.parse(raw);
            // Check if consent has expired (6 months)
            if (data.expiry && new Date(data.expiry) < new Date()) {
                localStorage.removeItem('analytics_consent');
                return; // expired — show banner again
            }
            setConsentGiven(data.value === true);
        } catch {
            // Legacy format or corrupted — clear and re-ask
            localStorage.removeItem('analytics_consent');
        }
    }, []);

    // Track event function
    const trackEvent = useCallback(async (eventType, data = {}) => {
        // Only track if consent given
        if (!consentGiven) return;

        try {
            const payload = {
                event_type: eventType,
                session_id: sessionId,
                device_type: deviceType,
                referrer: document.referrer || null,
                ...data
            };

            await fetch(`${API_URL}/api/analytics/event`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
        } catch (err) {
            // Silently fail - analytics should not break the app
            console.debug('Analytics event failed:', err);
        }
    }, [consentGiven, sessionId, deviceType]);

    // Listen for cart events (from CartContext which can't use this context directly)
    useEffect(() => {
        const handleCartEvent = (event) => {
            const { eventType, ...data } = event.detail;
            trackEvent(eventType, data);
        };
        
        window.addEventListener('track-analytics', handleCartEvent);
        return () => window.removeEventListener('track-analytics', handleCartEvent);
    }, [trackEvent]);

    // Convenience methods
    const trackPageView = useCallback((page) => {
        trackEvent('page_view', { page });
    }, [trackEvent]);

    const trackProductView = useCallback((productId, category) => {
        trackEvent('product_view', { product_id: productId, category });
    }, [trackEvent]);

    const trackAddToCart = useCallback((productId, value) => {
        trackEvent('add_to_cart', { product_id: productId, value });
    }, [trackEvent]);

    const trackRemoveFromCart = useCallback((productId) => {
        trackEvent('remove_from_cart', { product_id: productId });
    }, [trackEvent]);

    const trackSearch = useCallback((query) => {
        trackEvent('search', { search_query: query });
    }, [trackEvent]);

    const trackCheckoutStart = useCallback((value) => {
        trackEvent('checkout_start', { value });
    }, [trackEvent]);

    const trackPurchase = useCallback((orderId, value) => {
        trackEvent('purchase', { product_id: orderId, value });
    }, [trackEvent]);

    const trackCategoryView = useCallback((category) => {
        trackEvent('category_view', { category });
    }, [trackEvent]);

    // Handle consent
    const saveConsent = useCallback((value) => {
        const expiry = new Date();
        expiry.setMonth(expiry.getMonth() + 6); // 6-month expiry
        localStorage.setItem('analytics_consent', JSON.stringify({ value, expiry: expiry.toISOString() }));
        setConsentGiven(value);
    }, []);

    const acceptCookies = useCallback(() => saveConsent(true), [saveConsent]);
    const declineCookies = useCallback(() => saveConsent(false), [saveConsent]);

    const resetConsent = useCallback(() => {
        localStorage.removeItem('analytics_consent');
        setConsentGiven(null);
    }, []);

    return (
        <AnalyticsContext.Provider value={{
            consentGiven,
            acceptCookies,
            declineCookies,
            resetConsent,
            trackPageView,
            trackProductView,
            trackAddToCart,
            trackRemoveFromCart,
            trackSearch,
            trackCheckoutStart,
            trackPurchase,
            trackCategoryView,
            trackEvent
        }}>
            {children}
        </AnalyticsContext.Provider>
    );
}

export function useAnalytics() {
    const context = useContext(AnalyticsContext);
    if (!context) {
        throw new Error('useAnalytics must be used within AnalyticsProvider');
    }
    return context;
}
